/**
 * Worked cost comparison data.
 *
 * Single source of truth for the figures shown in the pricing-page comparison
 * drawer and on /pricing/methodology. Keep all numbers, assumptions and source
 * references here — never inline them in components.
 *
 * Monthly-first. The headline compares:
 *   - the KleipData monthly price, and
 *   - ONE estimated monthly equivalent for the assembled alternative:
 *     the MIDPOINT of the worked year-one range ÷ 12.
 * The worked low/high range lives in the Cost Breakdown tab and the methodology
 * page only.
 *
 * The comparison is like-for-like: the same requirement is shown on the same
 * row for both sides, with an objective status. Where a capability is not
 * covered by the estimate the wording is always "not included in the displayed
 * estimate" — never "not available" / "doesn't offer".
 *
 * All KleipData amounts are exclusive of VAT. Alternative figures are presented
 * on the same ex-VAT basis. No VAT is calculated anywhere in the UI.
 */

export const METHODOLOGY_REVIEWED = "30 August 2026";

export type PlanKey = "small" | "team";
export type IconKey = string;

export type WorkedComparison = {
  key: PlanKey;
  planName: string; // "KleipData Small"
  planShort: string; // "KleipData Small"
  drawerTitle: string; // "10-user cost comparison"
  users: string; // "Up to 10 users"
  userCount: string; // "10"

  monthly: string; // "£4,800"
  monthlyNum: number;

  /** Single headline estimate for the alternative: year-one midpoint ÷ 12. */
  estMonthly: string; // "£7,908"
  estMonthlyNum: number;

  /** Supporting detail (Cost Breakdown tab / methodology only). */
  annualMidpoint: string; // "£94,900"
  rangeLow: string; // "£87,400"
  rangeHigh: string; // "£102,400"
  equivLow: string; // "£7,283"
  equivHigh: string; // "£8,533"
  annualised: string; // "£57,600"

  kyc: {
    callsPerYear: string;
    calc: string;
    annual: string;
    monthlyAvg: string;
  };

  assumptions: { label: string; icon: IconKey }[];
};

export const COMPARISONS: Record<PlanKey, WorkedComparison> = {
  small: {
    key: "small",
    planName: "KleipData Small",
    planShort: "KleipData Small",
    drawerTitle: "10-user cost comparison",
    users: "Up to 10 users",
    userCount: "10",
    monthly: "£4,800",
    monthlyNum: 4800,
    estMonthly: "£7,908",
    estMonthlyNum: 7908,
    annualMidpoint: "£94,900",
    rangeLow: "£87,400",
    rangeHigh: "£102,400",
    equivLow: "£7,283",
    equivHigh: "£8,533",
    annualised: "£57,600",
    kyc: {
      callsPerYear: "2,500 calls / year",
      calc: "2,500 × £1.50",
      annual: "£3,750",
      monthlyAvg: "£312.50",
    },
    assumptions: [
      { label: "10 users", icon: "licence" },
      { label: "10,000 operational workflows / year", icon: "transactions" },
      { label: "API integration required", icon: "api" },
      { label: "CRM integration required", icon: "integration" },
      { label: "10 consultancy / implementation days", icon: "services" },
    ],
  },
  team: {
    key: "team",
    planName: "KleipData Team",
    planShort: "KleipData Team",
    drawerTitle: "20-user cost comparison",
    users: "Up to 20 users",
    userCount: "20",
    monthly: "£7,995",
    monthlyNum: 7995,
    estMonthly: "£13,438",
    estMonthlyNum: 13438,
    annualMidpoint: "£161,250",
    rangeLow: "£150,000",
    rangeHigh: "£172,500",
    equivLow: "£12,500",
    equivHigh: "£14,375",
    annualised: "£95,940",
    kyc: {
      callsPerYear: "10,000 calls / year",
      calc: "10,000 × £1.50",
      annual: "£15,000",
      monthlyAvg: "£1,250",
    },
    assumptions: [
      { label: "20 users", icon: "licence" },
      { label: "25,000 operational workflows / year", icon: "transactions" },
      { label: "API integration required", icon: "api" },
      { label: "CRM integration required", icon: "integration" },
      { label: "15 consultancy / implementation days", icon: "services" },
    ],
  },
};

/* ---------------------------------------------------------- headline copy */

export const CANVAS_SUBTITLE =
  "Same operational requirement. Two ways to procure it.";

export const ALT_HEADING = "Estimated assembled alternative";
export const ALT_SUBHEADING = "Worked market comparison";

export const EST_MONTHLY_NOTE =
  "This estimate is the midpoint of the worked cost range used in our comparison methodology, divided by 12 to give a monthly equivalent. It does not imply that alternative providers invoice this amount monthly.";

export const KLEIP_PRICE_CONTEXT =
  "Includes platform, intelligence, API, integration and implementation support.";
export const KLEIP_PRICE_CONTEXT_KYC =
  "KYC / AML is usage based at £1.50 per call.";

export const TWELVE_MONTH_NOTE =
  "12-month equivalent, for comparison only. KleipData remains monthly with no fixed-term commitment.";

/* ------------------------------------------------------ status vocabulary */

export type Status =
  | "included" // KleipData, in the bundled price
  | "included-note" // KleipData, in the price (footnoted)
  | "included-est" // Alternative, part of the displayed estimate
  | "costed-est" // Alternative, modelled as an implementation / PS cost
  | "not-in-est" // Alternative, not in the displayed estimate
  | "usage" // usage based (£1.50 / call)
  | "varies";

export const STATUS_LABEL: Record<Status, string> = {
  included: "Included",
  "included-note": "Included*",
  "included-est": "Included in estimate",
  "costed-est": "Costed in estimate",
  "not-in-est": "Not included in estimate",
  usage: "£1.50 / call",
  varies: "Varies",
};

/* --------------------------------------------- mirrored "what's included" */

export type MirrorRow = {
  requirement: string;
  icon: IconKey;
  kleipdata: { status: Status; note?: string };
  alternative: { status: Status; note?: string };
};

/** Same requirement per row on both sides. ~9 critical rows. */
export function mirroredMatrix(d: WorkedComparison): MirrorRow[] {
  return [
    {
      requirement: "Platform",
      icon: "platform",
      kleipdata: { status: "included", note: d.users },
      alternative: {
        status: "included-est",
        note: `${d.userCount}-user licence assumption`,
      },
    },
    {
      requirement: "Core intelligence",
      icon: "intelligence",
      kleipdata: { status: "included-note", note: "Unlimited included searches" },
      alternative: {
        status: "included-est",
        note: "Transactional / licensed usage assumption",
      },
    },
    {
      requirement: "Company intelligence",
      icon: "building",
      kleipdata: { status: "included-note" },
      alternative: { status: "not-in-est" },
    },
    {
      requirement: "Company credit",
      icon: "filechart",
      kleipdata: { status: "included-note" },
      alternative: { status: "not-in-est" },
    },
    {
      requirement: "Property / Land Registry",
      icon: "house",
      kleipdata: { status: "included-note" },
      alternative: { status: "not-in-est" },
    },
    {
      requirement: "API",
      icon: "api",
      kleipdata: { status: "included" },
      alternative: {
        status: "included-est",
        note: "API setup / access costed",
      },
    },
    {
      requirement: "CRM & casework integration",
      icon: "workflow",
      kleipdata: { status: "included", note: "Within agreed implementation scope" },
      alternative: {
        status: "costed-est",
        note: "Implementation cost assumption",
      },
    },
    {
      requirement: "Consultancy & implementation",
      icon: "consultancy",
      kleipdata: { status: "included", note: "Within agreed implementation scope" },
      alternative: {
        status: "costed-est",
        note: "Professional-services assumption",
      },
    },
    {
      requirement: "KYC / AML",
      icon: "shield",
      kleipdata: { status: "usage", note: "Usage based" },
      alternative: { status: "not-in-est" },
    },
  ];
}

/* -------------------------------- canvas: what the estimate covers (compact) */

/** Three concise visual statements under the alternative price in the canvas. */
export const ALT_CANVAS_SUMMARY: { status: Status; items: string }[] = [
  { status: "included-est", items: "Platform · Core intelligence · API" },
  { status: "costed-est", items: "CRM integration · Professional services" },
  {
    status: "not-in-est",
    items: "Property / Land Registry · Company credit · KYC / AML",
  },
];

/* --------------------------------------------------- one takeaway panel */

export const TAKEAWAY_KLEIP =
  "Platform + intelligence + company + property / Land Registry + API + integration + consultancy included.*";
export const TAKEAWAY_ALT =
  "Platform licences + modelled core-search usage + API + CRM implementation + professional-services assumptions.";
export const TAKEAWAY_EXCLUDES = [
  "Property / Land Registry",
  "Company credit",
  "KYC / AML",
];

/* ------------------------------------------------ commercial-model summary */

export const COMMERCIAL_SUMMARY: {
  label: string;
  icon: IconKey;
  kleipdata: string;
  alternative: string;
}[] = [
  {
    label: "Billing",
    icon: "calendar",
    kleipdata: "Monthly",
    alternative: "Estimated monthly equivalent of worked year-one costs",
  },
  {
    label: "Commitment",
    icon: "unlock",
    kleipdata: "No fixed term",
    alternative: "Terms vary according to selected services / providers",
  },
  {
    label: "Pricing structure",
    icon: "layers",
    kleipdata: "One bundled monthly price",
    alternative:
      "Licence + usage + API + implementation + professional services",
  },
];

/* --------------------------------------------------- service scope matrix */

export type ScopeRow = {
  label: string;
  icon: IconKey;
  kleipdata: Status;
  alternative: Status;
};

export const SERVICE_SCOPE: ScopeRow[] = [
  { label: "People intelligence", icon: "licence", kleipdata: "included", alternative: "included-est" },
  { label: "Address intelligence", icon: "house", kleipdata: "included", alternative: "included-est" },
  { label: "Connections", icon: "workflow", kleipdata: "included", alternative: "included-est" },
  { label: "Company intelligence", icon: "building", kleipdata: "included-note", alternative: "not-in-est" },
  { label: "Company credit", icon: "filechart", kleipdata: "included-note", alternative: "not-in-est" },
  { label: "Property", icon: "house", kleipdata: "included-note", alternative: "not-in-est" },
  { label: "Land Registry", icon: "house", kleipdata: "included-note", alternative: "not-in-est" },
  { label: "Contact", icon: "support", kleipdata: "included", alternative: "included-est" },
  { label: "Tracing", icon: "transactions", kleipdata: "included", alternative: "included-est" },
  { label: "Bulk", icon: "layers", kleipdata: "included", alternative: "included-est" },
  { label: "API", icon: "code", kleipdata: "included", alternative: "included-est" },
  { label: "CRM integration", icon: "integration", kleipdata: "included", alternative: "costed-est" },
  { label: "Casework integration", icon: "workflow", kleipdata: "included", alternative: "costed-est" },
  { label: "Consultancy", icon: "consultancy", kleipdata: "included", alternative: "costed-est" },
  { label: "Support", icon: "support", kleipdata: "included", alternative: "varies" },
];

/* ------------------------------------------------------------------- sources */

export type PricingSource = {
  provider: string;
  service: string;
  publishedPrice: string;
  sourceUrl: string;
  dateReviewed: string;
  notes: string;
};

/**
 * Public pricing sources underpinning the estimated assembled alternative.
 *
 * TODO(kleipdata): populate each entry with the confirmed public source before
 * the methodology page is treated as final. Entries with an empty `sourceUrl`
 * render on /pricing/methodology as "to be confirmed" behind a "being finalised"
 * banner. Do not present unconfirmed figures as sourced.
 */
export const PRICING_SOURCES: PricingSource[] = [
  {
    provider: "To be confirmed",
    service: "User / platform licence (identity & intelligence platform)",
    publishedPrice: "To be confirmed",
    sourceUrl: "",
    dateReviewed: METHODOLOGY_REVIEWED,
    notes:
      "Per-user or per-seat platform licence used for the platform / user licence component of the assembled alternative.",
  },
  {
    provider: "To be confirmed",
    service: "Transactional identity / trace / occupancy search",
    publishedPrice: "To be confirmed",
    sourceUrl: "",
    dateReviewed: METHODOLOGY_REVIEWED,
    notes:
      "Per-search or banded transactional pricing applied to the stated annual operational workflow volume.",
  },
  {
    provider: "To be confirmed",
    service: "API access / setup",
    publishedPrice: "To be confirmed",
    sourceUrl: "",
    dateReviewed: METHODOLOGY_REVIEWED,
    notes: "One-off and/or recurring API access charges.",
  },
  {
    provider: "To be confirmed",
    service: "CRM / workflow integration and implementation",
    publishedPrice: "To be confirmed",
    sourceUrl: "",
    dateReviewed: METHODOLOGY_REVIEWED,
    notes:
      "Professional-services day rate applied to the stated number of implementation days.",
  },
];

export const SOURCES_CONFIRMED = PRICING_SOURCES.every(
  (s) => s.sourceUrl.trim() !== "",
);

/* --------------------------------------------------------------- disclaimers */

export const DRAWER_DISCLAIMER =
  "Illustrative comparison only. Figures are based on stated usage assumptions and publicly available market pricing. The estimated assembled alternative is not a quotation from any individual supplier. Capabilities marked “not included in estimate” are not costed in the displayed figure — this is not a statement about what any provider can supply. Actual costs vary by supplier, usage, negotiated discounts, configuration, implementation requirements and contractual terms.";

export const DRAWER_VAT_LINE = "Prices ex VAT · Illustrative comparison";

/* ----------------------------------------------- methodology page helpers */

export const MODEL_KLEIPDATA = [
  "Platform",
  "Intelligence",
  "API",
  "Integration",
  "Consultancy",
];
export const MODEL_ASSEMBLED = [
  "Platform / user licences",
  "Transactional intelligence usage",
  "API setup / access",
  "CRM implementation",
  "Professional services",
];
